/**
 * Theme runtime: writes brand color CSS vars to the document root and
 * persists the active palette to localStorage so admin design choices
 * apply across the platform (Shishyastra admin / vendor / storefront
 * if they wire the same loader).
 */

export interface BrandPalette {
  id: string;
  name: string;
  description: string;
  /** Tailwind/preset key — used by some labels */
  key: string;
  /** Display swatch (hex) */
  swatch: string;
  /** HSL components: hue, saturation%, lightness% — applied to --primary and --ring */
  hsl: { h: number; s: number; l: number };
  /** Optional secondary accent HSL (drives --secondary). Defaults to saffron. */
  accent?: { h: number; s: number; l: number };
}

export const PALETTES: BrandPalette[] = [
  {
    id: 'grass-green',
    name: 'Grass Green',
    description: 'Fresh, natural, default Shishyastra brand.',
    key: 'green',
    swatch: '#4F8E22',
    hsl: { h: 92, s: 61, l: 34 },
  },
  {
    id: 'forest-emerald',
    name: 'Forest Emerald',
    description: 'Deeper, more premium green.',
    key: 'emerald',
    swatch: '#059669',
    hsl: { h: 160, s: 84, l: 31 },
  },
  {
    id: 'lime-zest',
    name: 'Lime Zest',
    description: 'Bright, energetic, festival-friendly.',
    key: 'lime',
    swatch: '#65A30D',
    hsl: { h: 84, s: 84, l: 35 },
  },
  {
    id: 'crimson',
    name: 'Himalayan Crimson',
    description: 'Original NepalMart palette, warm and bold.',
    key: 'crimson',
    swatch: '#BE123C',
    hsl: { h: 347, s: 82, l: 41 },
  },
  {
    id: 'cobalt',
    name: 'Cobalt Trust',
    description: 'Professional, trustworthy, banking-style.',
    key: 'cobalt',
    swatch: '#1D4ED8',
    hsl: { h: 224, s: 76, l: 48 },
  },
  {
    id: 'saffron',
    name: 'Saffron Glow',
    description: 'Festive, sunset, warm.',
    key: 'saffron',
    swatch: '#D97706',
    hsl: { h: 32, s: 95, l: 44 },
  },
  {
    id: 'royal-violet',
    name: 'Royal Violet',
    description: 'Modern, premium, luxe.',
    key: 'violet',
    swatch: '#7C3AED',
    hsl: { h: 262, s: 83, l: 58 },
  },
  {
    id: 'ocean-teal',
    name: 'Ocean Teal',
    description: 'Calm, wellness, eco-friendly.',
    key: 'teal',
    swatch: '#0D9488',
    hsl: { h: 173, s: 84, l: 32 },
  },
];

export const DEFAULT_PALETTE_ID = 'grass-green';
const STORAGE_KEY = 'shishyastra.theme.v1';

export interface ThemeState {
  paletteId: string;
  /** Optional manual HSL override (when admin customizes a palette) */
  customHsl?: { h: number; s: number; l: number };
  radius?: number;
}

export function loadTheme(): ThemeState {
  if (typeof window === 'undefined') return { paletteId: DEFAULT_PALETTE_ID };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { paletteId: DEFAULT_PALETTE_ID };
    const parsed = JSON.parse(raw) as ThemeState;
    return { ...parsed, paletteId: parsed.paletteId ?? DEFAULT_PALETTE_ID };
  } catch {
    return { paletteId: DEFAULT_PALETTE_ID };
  }
}

export function saveTheme(state: ThemeState): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function findPalette(id: string): BrandPalette {
  return PALETTES.find((p) => p.id === id) ?? PALETTES[0];
}

export function applyTheme(state: ThemeState): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const palette = findPalette(state.paletteId);
  const hsl = state.customHsl ?? palette.hsl;
  const hslStr = `${hsl.h} ${hsl.s}% ${hsl.l}%`;
  root.style.setProperty('--primary', hslStr);
  root.style.setProperty('--ring', hslStr);
  if (palette.accent) {
    root.style.setProperty('--secondary', `${palette.accent.h} ${palette.accent.s}% ${palette.accent.l}%`);
  }
  if (state.radius != null) {
    root.style.setProperty('--radius', `${state.radius}rem`);
  }
  root.setAttribute('data-theme', palette.id);
}

export function resetTheme(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.removeProperty('--primary');
  root.style.removeProperty('--ring');
  root.style.removeProperty('--secondary');
  root.style.removeProperty('--radius');
  root.removeAttribute('data-theme');
  localStorage.removeItem(STORAGE_KEY);
}
